import { keyframes, styled } from "goober";
import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useMouseDownOutside } from "../hooks/useMouseDownOutside";
import { zIndexes } from "../lib/zIndexes";
import { Button } from "./Button";
import { Icon, type IconName } from "./Icon";
import { Keycap } from "./Keycap";
import { Text } from "./Text";

type Entry = { description: string; keys: string[] };
type Group = { label: string; entries: Entry[] };

const GROUPS: Group[] = [
  {
    label: "Global",
    entries: [
      { keys: ["cmd", "k"], description: "Open command bar" },
      { keys: ["cmd", "j"], description: "Open 3B activity" },
      { keys: ["cmd", "/", "or", "?"], description: "Open shortcuts and tips" },
      { keys: ["cmd", "\\"], description: "Toggle sidebar" },
      { keys: ["shift", "option", "d"], description: "Toggle light/dark mode" },
    ],
  },
  {
    label: "Go to",
    entries: [
      { keys: ["g", "then", "r"], description: "Recents" },
      { keys: ["g", "then", "b"], description: "Branches" },
      { keys: ["g", "then", "f"], description: "Favorites" },
      { keys: ["g", "then", "c"], description: "Chat" },
      { keys: ["g", "then", "p"], description: "Personal workflows" },
      { keys: ["g", "then", "o"], description: "Connectors" },
      { keys: ["g", "then", "k"], description: "Skills" },
      { keys: ["g", "then", "m"], description: "Monitoring" },
      { keys: ["g", "then", "s"], description: "Settings" },
      { keys: ["g", "then", "1"], description: "Feature flags" },
    ],
  },
  {
    label: "Chat",
    entries: [
      { keys: ["/"], description: "Focus chat" },
      { keys: ["esc"], description: "Unfocus chat" },
      { keys: ["cmd", "enter"], description: "Force send (interrupt reply)" },
      { keys: ["shift", "enter"], description: "New line" },
      { keys: ["cmd", "shift", "g"], description: "Follow 3B" },
      { keys: ["type", "/"], description: "Open AI suggestions" },
      { keys: ["drag", "or", "paste"], description: "Attach files" },
      { keys: ["highlight"], description: "Add selected code as context" },
      { keys: ["cmd", "shift", "."], description: "Toggle composer controls" },
      { keys: ["shift", "option", "p"], description: "Toggle plan mode" },
    ],
  },
  {
    label: "Step map",
    entries: [
      { keys: ["shift", "option", "v"], description: "Open branch selector" },
      {
        keys: ["arrow-up", "arrow-down", "arrow-left", "arrow-right"],
        description: "Navigate steps",
      },
      {
        keys: ["right-click"],
        description: "Step: change color, copy URL, run options",
      },
    ],
  },
  {
    label: "Step editor",
    entries: [
      { keys: ["cmd", "shift", "1"], description: "Toggle code view" },
      { keys: ["cmd", "shift", "2"], description: "Toggle runs view" },
      { keys: ["shift", "option", "i"], description: "Toggle inspect mode" },
      {
        keys: ["right-click"],
        description: "Connector icon: manage, remove from step",
      },
    ],
  },
  {
    label: "Code view",
    entries: [
      { keys: ["cmd", "shift", "f"], description: "Find in code" },
      { keys: ["cmd", "shift", "d"], description: "Toggle diffs" },
      { keys: ["p"], description: "Previous diff" },
      { keys: ["n"], description: "Next diff" },
    ],
  },
  {
    label: "Runs view",
    entries: [
      { keys: ["option", "cmd", "1"], description: "Output tab" },
      { keys: ["option", "cmd", "2"], description: "Input tab" },
      { keys: ["option", "cmd", "3"], description: "Logs tab" },
      { keys: ["option", "cmd", "4"], description: "Metadata tab" },
      { keys: ["option", "cmd", "6"], description: "Requests tab" },
    ],
  },
  {
    label: "File browser",
    entries: [
      { keys: ["arrow-up", "arrow-down"], description: "Move within column" },
      {
        keys: ["arrow-right", "or", "enter"],
        description: "Open or next column",
      },
      { keys: ["arrow-left"], description: "Back to parent column" },
      { keys: ["space"], description: "Toggle preview" },
      { keys: ["esc"], description: "Close browser" },
    ],
  },
];

const slideIn = keyframes`
  from {
    transform: translateX(16px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const Container = styled("div")`
  position: fixed;
  top: 12px;
  right: 12px;
  bottom: 12px;
  width: 320px;
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  box-shadow: var(--elevation-high);
  border-radius: 16px;
  z-index: ${zIndexes.keyboardShortcuts};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${slideIn} 180ms ease-out;
`;

const HeaderRow = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10.5px 12px 10px;
  box-shadow: 0 0.5px 0 var(--border-alpha);
  flex-shrink: 0;
`;

const ScrollArea = styled("div")`
  flex: 1;
  overflow-y: auto;
  overscroll-behavior: none;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Section = styled("div")`
  display: flex;
  flex-direction: column;
`;

const SectionHeading = styled("h4")`
  margin: 0;
  padding: 0 0 6px;
`;

const Row = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
`;

const Keys = styled("div")`
  display: flex;
  align-items: center;
  gap: 2px;
  margin-left: auto;
`;

const ARROW_ICONS: Record<string, IconName> = {
  "arrow-up": "arrow-up",
  "arrow-down": "arrow-down",
  "arrow-left": "arrow-left",
  "arrow-right": "arrow-right",
  enter: "corner-down-left",
};

const SYMBOLS: Record<string, string> = {
  cmd: "⌘",
  shift: "⇧",
  option: "⌥",
  ctrl: "⌃",
};

const CONNECTING_WORDS = new Map<string, string>([
  ["then", "then"],
  ["or", "or"],
  ["type", "Type"],
]);

function capitalize(token: string): string {
  return token.charAt(0).toUpperCase() + token.slice(1);
}

function KeyToken({ token }: { token: string }) {
  const iconName = ARROW_ICONS[token];
  if (iconName) {
    return (
      <Keycap>
        <Icon name={iconName} size={12} />
      </Keycap>
    );
  }
  const display =
    SYMBOLS[token] ??
    (token.length === 1 ? token.toUpperCase() : capitalize(token));
  return (
    <Keycap style={{ padding: "2px 4px" }}>
      <Text variant="text-smaller-md">{display}</Text>
    </Keycap>
  );
}

type Props = {
  open: boolean;
  onClose: () => void;
};

export function KeyboardShortcutsPanel({ open, onClose }: Props) {
  const [containerRef] = useMouseDownOutside(() => {
    if (open) onClose();
  });

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <Container ref={containerRef}>
      <HeaderRow>
        <Text variant="text-regular-md">Shortcuts and tips</Text>
        <Button variant="secondary" icon="x" onClick={onClose} />
      </HeaderRow>
      <ScrollArea>
        {GROUPS.map((group) => (
          <Section key={group.label}>
            <SectionHeading>
              <Text variant="text-small" color="var(--text-secondary)">
                {group.label}
              </Text>
            </SectionHeading>
            {group.entries.map((entry) => (
              <Row key={entry.description + entry.keys.join("+")}>
                <Text ellipsis>{entry.description}</Text>
                <Keys>
                  {entry.keys.map((token, index) => {
                    const key = `${token}-${index}`;
                    const word = CONNECTING_WORDS.get(token);
                    if (word) {
                      return (
                        <Text
                          key={key}
                          variant="text-smaller-md"
                          color="var(--text-secondary)"
                        >
                          {word}
                        </Text>
                      );
                    }
                    return <KeyToken key={key} token={token} />;
                  })}
                </Keys>
              </Row>
            ))}
          </Section>
        ))}
      </ScrollArea>
    </Container>,
    document.body
  );
}
